import React, {
  ChangeEvent,
  Fragment,
  ReactElement,
  useState,
  useEffect,
} from 'react'
import { CountryCallingCode } from 'libphonenumber-js'
import styled from 'styled-components'
import colors from '../lib/colors'
import FieldStyles, { FieldSize } from '../components/FieldStyles'
import { hasForbiddenChars } from '../helpers/fieldHelper'
import { countryArray, Country } from '../helpers/phoneHelper'

const CountryListItem = styled.div`
  padding: 4px 6px;
  cursor: pointer;
  &:hover {
    background-color: ${colors.lightGray};
  }
`
const Dropdown = styled.div`
  background-color: ${colors.white};
`
const Widget = styled.div`
  width: 45px;
  &.widget-large {
    width: 50px;
  }
`
export type PhoneNumber = {
  dialingCode: CountryCallingCode
  phoneNumber: string
}
interface IPhoneFieldProps {
  name?: string
  placeholder?: string
  value?: PhoneNumber
  onChange?: (value: PhoneNumber) => void
  className?: string
  isError?: boolean
  label?: string
  isLong?: boolean
  disabled?: boolean
  icon?: React.SVGProps<SVGElement>
  subtitle?: string
  style?: object
  autoComplete?: boolean
  fieldSize?: FieldSize
}
export default function PhoneField(props: IPhoneFieldProps): ReactElement {
  const {
    name,
    placeholder,
    isError,
    value,
    onChange,
    label,
    isLong,
    disabled,
    className,
    icon,
    subtitle,
    style,
    autoComplete,
    fieldSize,
  } = props

  const notPhoneNumberChars = (input: string): boolean => {
    const regEx = /[^0-9()+\- ]/
    return regEx.test(input)
  }

  // sort alphabetically, then duplicate UK and US and put at start
  countryArray.sort((a, b) => {
    if (a.name < b.name) return -1
    if (a.name > b.name) return 1
    return 0
  })
  countryArray.unshift(
    countryArray[
      countryArray.findIndex((country) => country.countryCode === 'US')
    ],
  )
  countryArray.unshift(
    countryArray[
      countryArray.findIndex((country) => country.countryCode === 'GB')
    ],
  )
  const existing = countryArray.find(
    (country) => country.dialingCode === value?.dialingCode,
  )
  const [currentCountry, setCurrentCountry] = useState<Country>(
    existing || countryArray[0],
  )
  const { dialingCode } = currentCountry

  const errorClass = isError ? 'error' : ''
  const longClass = isLong ? 'input-long' : ''
  const classes = `styled-input input-styles with-widget ${errorClass} ${longClass} ${className}`

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value: newValue } = event.target

    if (hasForbiddenChars(newValue)) return
    if (notPhoneNumberChars(newValue)) return
    if (newValue.length > 15) return

    if (onChange) {
      onChange({
        dialingCode,
        phoneNumber: newValue,
      })
    }
  }

  const [menu, setMenu] = useState(false)
  const handleKeyDown = (keyDownEvent: KeyboardEvent): void => {
    if (menu) {
      const search = `${keyDownEvent.key}-country`.toLowerCase()
      const target = document.getElementsByClassName(search)
      target[0].scrollIntoView({ behavior: 'auto' })
    }
  }
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menu])

  const disabledClass = disabled ? 'disabled' : ''
  const sizeClass = fieldSize ? `widget-${fieldSize}` : ''

  return (
    <FieldStyles label={label} subtitle={subtitle} fieldSize={fieldSize}>
      <div className="widget-wrapper">
        <Widget
          className={`widget clickable ${disabledClass} ${sizeClass}`}
          onClick={(): void => {
            setMenu(true)
          }}
          onKeyPress={(): void => {
            setMenu(false)
          }}
          role="button"
          aria-label="Close cover"
        >
          {`+${currentCountry?.dialingCode}`}
        </Widget>
        <input
          type="tel"
          name={name}
          placeholder={placeholder}
          className={classes}
          value={value?.phoneNumber || ''}
          onChange={handleChange}
          disabled={disabled}
          style={style}
          autoComplete={autoComplete ? 'tel-national' : undefined}
        />
        <div className="input-icon">{icon}</div>
      </div>
      {menu && (
        <Fragment>
          <div
            className="dropdown-cover"
            onClick={(): void => {
              setMenu(false)
            }}
            onKeyPress={(): void => {
              setMenu(false)
            }}
            role="button"
            tabIndex={0}
            aria-label="Close cover"
          />
          <Dropdown id="scrollingDropdown" className="dropdown with-items">
            {countryArray.map((country, index) => {
              const key = `${country.countryCode}${index}`
              return (
                <CountryListItem
                  key={key}
                  className={`${country.name[0]}-country`.toLowerCase()}
                  onClick={(): void => {
                    if (onChange) {
                      onChange({
                        dialingCode: country.dialingCode,
                        phoneNumber: value ? value.phoneNumber : '',
                      })
                    }
                    setCurrentCountry(country)
                    setMenu(false)
                  }}
                >
                  {country.name}
                </CountryListItem>
              )
            })}
          </Dropdown>
        </Fragment>
      )}
    </FieldStyles>
  )
}
